import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { SkipLink } from './components/a11y/SkipLink';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import Header from './components/Header';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { PerformanceDashboard } from './components/PerformanceDashboard';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './contexts/AuthContext';

// 遅延読み込みするページコンポーネント
const Home = lazy(() => import('./pages/Home'));
const MyPage = lazy(() => import('./pages/MyPage'));
const RecruitmentDetail = lazy(() => import('./pages/RecruitmentDetail'));
const RecruitmentList = lazy(() => import('./pages/RecruitmentList'));
const RegisterRecruitment = lazy(() => import('./pages/RegisterRecruitment'));
const RegisterTutor = lazy(() => import('./pages/RegisterTutor'));
const TutorDetail = lazy(() => import('./pages/TutorDetail'));
const TutorList = lazy(() => import('./pages/TutorList'));
const UserSettings = lazy(() => import('./pages/UserSettings'));

// ローディングコンポーネント
const Loading = () => (
  <output className="flex items-center justify-center min-h-[400px]">
    <div
      className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
      aria-hidden="true"
    ></div>
    <span className="sr-only">読み込み中...</span>
  </output>
);

// ページタイトルを取得する関数
function getPageTitle(pathname: string): string {
  const baseTitle = 'まなぶっと - オンライン家庭教師マッチングサービス';

  switch (pathname) {
    case '/':
      return baseTitle;
    case '/tutors':
      return `講師一覧 | ${baseTitle}`;
    case '/recruitments':
      return `生徒募集一覧 | ${baseTitle}`;
    case '/register/tutor':
      return `講師登録 | ${baseTitle}`;
    case '/register/recruitment':
      return `生徒募集登録 | ${baseTitle}`;
    case '/settings':
      return `設定 | ${baseTitle}`;
    case '/mypage':
      return `マイページ | ${baseTitle}`;
    default:
      if (pathname.startsWith('/tutors/')) {
        return `講師詳細 | ${baseTitle}`;
      } else if (pathname.startsWith('/recruitments/')) {
        return `生徒募集詳細 | ${baseTitle}`;
      }
      return baseTitle;
  }
}

function App() {
  const location = useLocation();

  // ルート変更時にページトップへフォーカスを移動
  useEffect(() => {
    const mainElement = document.getElementById('main-content');
    if (mainElement) {
      mainElement.focus();
    }
    // ページタイトルをアナウンス
    document.title = getPageTitle(location.pathname);
  }, [location]);

  return (
    <GlobalErrorBoundary resetKeys={[location.pathname]}>
      <AuthProvider>
        <div className="min-h-screen bg-gray-50">
          <SkipLink href="#main-content">メインコンテンツへスキップ</SkipLink>
          <MaintenanceBanner />
          <Header />
          <main
            id="main-content"
            tabIndex={-1}
            className="container mx-auto px-2 py-2 md:px-4 md:py-8 focus:outline-none"
          >
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/tutors" element={<TutorList />} />
                <Route path="/tutors/:id" element={<TutorDetail />} />
                <Route path="/recruitments" element={<RecruitmentList />} />
                <Route path="/recruitments/:id" element={<RecruitmentDetail />} />
                <Route
                  path="/register/tutor"
                  element={
                    <PrivateRoute>
                      <RegisterTutor />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/register/recruitment"
                  element={
                    <PrivateRoute>
                      <RegisterRecruitment />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <UserSettings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/mypage"
                  element={
                    <PrivateRoute>
                      <MyPage />
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Suspense>
          </main>
          <PerformanceDashboard />
        </div>
      </AuthProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
